import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { VerticalConfig, VerificationMeta } from './atnaConfig';
export type Bi = {
  en: string;
  fr: string;
};
export type Label = {
  grade: 'A' | 'B' | 'C' | 'D';
  vi: number;
  source: number;
  concordance: number;
  freshness: number;
  type: 'FACT' | 'ESTIMATE';
  flags: Bi[];
  concordanceNote: Bi;
};
const F_ESTIMATE: Bi = {
  en: 'Illustrative',
  fr: 'Illustratif'
};
export const QUESTION_FR: Record<string, string> = {
  'How is ATNA different from a chatbot?': "En quoi ATNA se distingue-t-elle d'un robot conversationnel ?",
  'How can ATNA help my organization do more with the same team?': 'Comment ATNA aide-t-elle mon organisation à faire plus avec la même équipe ?',
  'Which industries can use BCITI + ATNA?': 'Quels secteurs peuvent utiliser BCITI + ATNA ?',
  'Can BCITI integrate with our existing systems?': "BCITI peut-elle s'intégrer à nos systèmes existants ?",
  'How long does deployment take?': 'Combien de temps prend le déploiement ?',
  'How does ATNA verify its answers?': 'Comment ATNA vérifie-t-elle ses réponses ?',
  'Can ATNA perform actions instead of only answering questions?': 'ATNA peut-elle exécuter des actions, pas seulement répondre ?',
  'Do you have customers in production?': 'Avez-vous des clients en production ?',
  'What business results can BCITI deliver?': "Quels résultats d'affaires BCITI peut-elle livrer ?",
  'Can I see a live demo?': 'Puis-je voir une démo en direct ?'
};
const SEARCH_ALIASES: Record<string, string[]> = {
  'How is ATNA different from a chatbot?': ['chatbot', 'bot', 'difference', 'different', 'assistant', 'robot', 'conversationnel', 'difference atna chatbot'],
  'How can ATNA help my organization do more with the same team?': ['capacity', 'team', 'same team', 'more with less', 'productivity', 'capacity team', 'equipe', 'capacité', 'faire plus'],
  'Which industries can use BCITI + ATNA?': ['industry', 'industries', 'sector', 'sectors', 'vertical', 'ville', 'health', 'airport', 'secteur', 'secteurs'],
  'Can BCITI integrate with our existing systems?': ['integrate', 'integration', 'api', 'crm', 'erp', 'sap', 'salesforce', 'legacy', 'system', 'systems', 'intégrer', 'integration', 'système'],
  'How long does deployment take?': ['deployment', 'deploy', 'timeline', 'how long', 'time', 'weeks', 'déploiement', 'combien', 'duree', 'durée'],
  'How does ATNA verify its answers?': ['verify', 'verification', 'verify answers', 'grade', 'score', 'source', 'concordance', 'verifie', 'vérifie', 'verification'],
  'Can ATNA perform actions instead of only answering questions?': ['action', 'actions', 'workflow', 'open request', 'form', 'schedule', 'execute', 'automate', 'exécuter', 'requête', 'formulaire'],
  'Do you have customers in production?': ['customer', 'customers', 'production', 'brossard', 'casselman', 'clarence', 'client', 'clients', 'preuve'],
  'What business results can BCITI deliver?': ['results', 'outcomes', 'roi', 'cost', 'costs', 'revenue', 'business', 'resultats', 'résultats', 'couts', 'coûts'],
  'Can I see a live demo?': ['demo', 'demonstration', 'live demo', 'meeting', 'book', 'démo', 'demonstration', 'rencontre']
};
export const questionLabel = (question: string, isFR: boolean) => isFR ? QUESTION_FR[question] ?? question : question;
export function normalizeSearchTerm(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/[^a-z0-9+\s]/g, ' ').replace(/\s+/g, ' ').trim();
}
export function findFaqQuestion(questions: string[], query: string) {
  const normalizedQuery = normalizeSearchTerm(query);
  if (!normalizedQuery) return null;
  const queryTokens = normalizedQuery.split(' ');
  let bestQuestion: string | null = null;
  let bestScore = 0;
  questions.forEach(question => {
    const searchable = [question, QUESTION_FR[question] ?? '', ...(SEARCH_ALIASES[question] ?? [])].map(normalizeSearchTerm).join(' ');
    const phraseMatch = searchable.includes(normalizedQuery);
    const tokenMatches = queryTokens.filter(token => token.length > 1 && searchable.includes(token)).length;
    const score = (phraseMatch ? 20 : 0) + tokenMatches;
    if (score > bestScore) {
      bestScore = score;
      bestQuestion = question;
    }
  });
  return bestScore > 0 ? bestQuestion : null;
}
function toLabel(meta: VerificationMeta): Label {
  return {
    grade: meta.grade,
    vi: meta.index,
    source: meta.source,
    concordance: meta.concordance,
    freshness: meta.freshness,
    type: 'ESTIMATE',
    flags: [F_ESTIMATE],
    concordanceNote: {
      en: 'Illustrative demonstration values',
      fr: 'Valeurs illustratives de démonstration'
    }
  };
}
export function resolveAnswer(vertical: VerticalConfig, question: string | null, isFR: boolean) {
  if (!question) return null;
  const answer = vertical.answers[question];
  if (!answer) return null;
  return {
    recommendation: isFR ? answer.recommendationFr : answer.recommendation,
    results: isFR ? answer.resultsFr : answer.results,
    caution: isFR ? answer.cautionFr : answer.caution,
    label: toLabel(answer.verification),
    modules: answer.modules
  };
}
export function VerificationScore({
  label,
  isFR,
  expanded,
  onToggle
}: {
  label: Label;
  isFR: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  return <div className="border border-[#E4E6EC] flex items-center justify-between px-4 py-3 gap-3" data-id="element-182">
      <div className="flex items-center gap-3 min-w-0" data-id="element-183">
        <div className="w-11 h-11 shrink-0 border border-[#4F46E5]/30 bg-[#4F46E5]/[0.04] flex items-center justify-center" data-id="element-184">
          <span className="font-serif text-lg text-[#4F46E5] leading-none" data-id="element-185">
            {label.vi.toFixed(2)}
          </span>
        </div>
        <div className="min-w-0" data-id="element-186">
          <div className="font-sans text-[11px] font-semibold tracking-[0.12em] uppercase text-[#5B6478]" data-id="element-187">
            {isFR ? 'Score de vérification' : 'Verification Score'}
          </div>
          <div className="font-sans text-sm text-[#121212]" data-id="element-188">
            Grade {label.grade} · {isFR ? 'Illustratif' : 'Illustrative'}
          </div>
        </div>
      </div>
      <button onClick={onToggle} className="shrink-0 inline-flex items-center gap-1 font-sans text-sm font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-189">
        {expanded ? isFR ? 'Masquer' : 'Hide' : isFR ? 'Voir les détails' : 'View details'}
        {expanded ? <ChevronUpIcon className="w-4 h-4" data-id="element-190" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-191" />}
      </button>
    </div>;
}
export function VerificationLabel({
  label,
  isFR,
  compact = false
}: {
  label: Label;
  isFR: boolean;
  compact?: boolean;
}) {
  const padding = compact ? 'px-4 py-2.5' : 'px-5 py-3';
  return <div className="border border-[#E4E6EC]" data-id="element-192">
      <div className={`${padding} border-b border-[#E4E6EC] font-sans text-xs font-semibold tracking-[0.15em] text-[#121212] uppercase`} data-id="element-193">
        {isFR ? 'Étiquette de vérification ATNA' : 'ATNA Verification Label'}
      </div>
      <div className="flex items-stretch border-b border-[#E4E6EC]" data-id="element-194">
        <div className={`flex items-center justify-center ${compact ? 'w-16' : 'w-24'} border-r border-[#E4E6EC] bg-[#4F46E5]/[0.03]`} data-id="element-195">
          <span className={`font-serif ${compact ? 'text-3xl' : 'text-4xl'} text-[#4F46E5]`} data-id="element-196">
            {label.grade}
          </span>
        </div>
        <div className={`flex-1 ${compact ? 'px-4 py-3' : 'px-5 py-4'}`} data-id="element-197">
          <div className="font-sans text-sm font-medium text-[#121212]" data-id="element-198">
            Grade {label.grade}
          </div>
          <div className="font-sans text-xs text-[#5B6478] mt-1" data-id="element-199">
            {isFR ? 'Indice de vérification' : 'Verification Index'}{' '}
            <span className="font-mono text-[#121212]" data-id="element-200">
              {label.vi.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
      <div className="divide-y divide-[#E4E6EC] border-b border-[#E4E6EC]" data-id="element-201">
        <AxisRow label="Source" value={label.source} compact={compact} data-id="element-202" />
        <AxisRow label="Concordance" value={label.concordance} compact={compact} data-id="element-203" />
        <AxisRow label={isFR ? 'Fraîcheur' : 'Freshness'} value={label.freshness} compact={compact} data-id="element-204" />
      </div>
      <div className={`flex items-center justify-between ${padding}`} data-id="element-205">
        <span className="font-sans text-sm text-[#5B6478]" data-id="element-206">
          {isFR ? 'Cadre' : 'Framing'}
        </span>
        <span className="font-sans text-sm text-[#121212]" data-id="element-207">
          {isFR ? 'Démonstration illustrative' : 'Illustrative demonstration'}
        </span>
      </div>
    </div>;
}
function AxisRow({
  label,
  value,
  compact
}: {
  label: string;
  value: number;
  compact?: boolean;
}) {
  return <div className={compact ? 'px-4 py-2.5' : 'px-5 py-3'} data-id="element-208">
      <div className="flex items-center justify-between mb-1.5" data-id="element-209">
        <span className="font-sans text-sm text-[#5B6478]" data-id="element-210">{label}</span>
        <span className="font-mono text-sm text-[#121212]" data-id="element-211">
          {value.toFixed(2)}
        </span>
      </div>
      <div className="h-1 bg-[#E4E6EC]" data-id="element-212">
        <div className="h-1 bg-[#4F46E5]" style={{
        width: `${Math.round(value * 100)}%`
      }} data-id="element-213" />
      </div>
    </div>;
}
export function WhyThis({
  label,
  isFR
}: {
  label: Label;
  isFR: boolean;
}) {
  return <div className="border border-[#E4E6EC] bg-[#FAFAFB] p-5" data-id="element-214">
      <div className="font-sans text-sm font-semibold text-[#121212] mb-4" data-id="element-215">
        {isFR ? 'Pourquoi cette réponse ?' : 'Why this answer?'}
      </div>
      <div className="space-y-2 font-sans text-sm" data-id="element-216">
        <div className="flex justify-between gap-4" data-id="element-217">
          <span className="text-[#5B6478]" data-id="element-218">Source</span>
          <span className="text-[#121212] text-right" data-id="element-219">
            {isFR ? 'Base de connaissances vérifiée + systèmes connectés' : 'Verified knowledge base + connected systems'}
          </span>
        </div>
        <div className="flex justify-between gap-4" data-id="element-220">
          <span className="text-[#5B6478]" data-id="element-221">Concordance</span>
          <span className="text-[#121212] text-right" data-id="element-222">
            {isFR ? label.concordanceNote.fr : label.concordanceNote.en}
          </span>
        </div>
        <div className="flex justify-between gap-4" data-id="element-223">
          <span className="text-[#5B6478]" data-id="element-224">
            {isFR ? 'Fraîcheur' : 'Freshness'}
          </span>
          <span className="text-[#121212] text-right" data-id="element-225">
            {isFR ? "Mis à jour aujourd'hui" : 'Updated today'}
          </span>
        </div>
      </div>
    </div>;
}
export function InformActNote({
  isFR
}: {
  isFR: boolean;
}) {
  return <div className="flex items-start gap-3" data-id="element-226">
      <CheckIcon className="w-4 h-4 text-[#4F46E5] shrink-0 mt-1" strokeWidth={2.25} data-id="element-227" />
      <p className="font-sans text-sm text-[#5B6478] leading-relaxed" data-id="element-228">
        {isFR ? "Réponse informative — l'humain décide sur l'étiquette divulguée. Toute action exige une autorisation ; ATNA n'exécute jamais automatiquement." : 'Informational answer — the human decides using the disclosed label. Any action requires authorization; ATNA never executes automatically.'}
      </p>
    </div>;
}
export function BuildingSequence({
  isFR
}: {
  isFR: boolean;
}) {
  return <motion.p initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="font-sans text-sm text-[#5B6478]" data-id="element-229">
      {isFR ? "ATNA prépare l'étiquette de vérification…" : 'ATNA is preparing the verification label…'}
    </motion.p>;
}